<template>
  <component
    :is="component"
    class="sub-section"
    :to="routeLink.path"
    :class="[{ 'as-header': !hasLink }, { 'as-link': hasLink }]"
    @click.capture="handleClickPathCustom"
  >
    <div class="logo" :class="{ default: !image }">
      <span class="image annale" :class="iconClass" :style="styleImage" />
      <span
        class="image annale shadow"
        :class="iconClass"
        :style="styleImage"
      />
    </div>
    <div class="details">
      <span class="title">
        {{ category.title }}
        <Icon
          v-if="!category.description && isTrialItem && hasDiamond"
          class="icon-gift"
          size="2rem"
          name="fluent-emoji-flat:wrapped-gift"
        />
        <icon
          v-else-if="!category.description && hasDiamond"
          name="nomad:diamond"
          size="1.8rem"
        />
      </span>
      <span v-if="category.description" class="description">
        {{ category.description }}
        <Icon
          v-if="isTrialItem && hasDiamond"
          class="icon-gift"
          size="2rem"
          name="fluent-emoji-flat:wrapped-gift"
        />
        <icon v-else-if="hasDiamond" name="nomad:diamond" size="1.8rem" />
      </span>
      <span v-if="labelChildrenLength" class="children-count">
        {{ labelChildrenLength }}
      </span>
    </div>
    <ChapterCommonProgress
      v-visible="exercisesCount"
      :count="exercisesDoneCount"
      :total="exercisesCount"
    />
    <div class="arrow">
      <icon class="graphic arrow-off" name="nextSmall" />
      <icon class="graphic arrow-on" name="nextSmall" />
    </div>
  </component>
</template>
<script setup lang="ts">
import type { NuxtLinkProps } from '#app';

interface Props extends NuxtLinkProps {
  category: CategorySchema;
  hasLink: boolean;
  itemsCount: number;
}

const props = defineProps({
  category: null,
  hasLink: { type: Boolean },
  itemsCount: null
});

const { currentContent, mPageMedias, categoryLockState } =
  inject(INJECT_CATEGORY_PAGE);

const { handleClickPath, isTrialItem, routeLink, linkedContent } =
  await useCategoryLinkTryAndLock(props.category);

const handleClickPathCustom = (e) => {
  if (props.hasLink && linkedContent.contentType !== ContentTypes.SubChapter) {
    return handleClickPath(e);
  }
};

const profile = useProfileStore();

const { progressions: allProgressions } = inject(INJECT_PROGRESSION_EXERCISES);

const exerciseIds = computed(() => {
  if (!props.category.exercises) return [];
  return props.category.exercises.map((exercise) => exercise.id);
});

const { exercisesDoneCount, exercisesCount } = useExercisesChapterProgressions(
  allProgressions,
  exerciseIds
);

const component = computed(() => {
  if (props.hasLink) {
    return resolveComponent('NuxtLink');
  }
  return 'div';
});

const iconClass = computed(() => {
  switch (props.category.childrenType) {
    case 'course_video':
      return 'course-video';
    case 'quiz':
      return 'quiz';
    default:
      return props.category.childrenType;
  }
});

const labelChildrenLength = computed(() => {
  switch (props.category.childrenType) {
    case 'course': {
      return `${props.itemsCount} cours`;
    }
    case 'course_video': {
      const suffix = props.itemsCount > 1 ? 'vidéos' : 'vidéo';
      return `${props.itemsCount} ${suffix}`;
    }
    case 'essential': {
      const suffix = props.itemsCount > 1 ? 'essentiels' : 'essentiel';
      return `${props.itemsCount} ${suffix}`;
    }

    case 'quiz':
      return `${props.itemsCount} quiz`;
    case 'practice': {
      const suffix = props.itemsCount > 1 ? 'pages' : 'page';
      return `${props.itemsCount} ${suffix}`;
    }
    case 'annals': {
      const suffix = props.itemsCount > 1 ? 'annales' : 'annale';
      return `${props.itemsCount} ${suffix}`;
    }
    case 'pairing':
    case 'fillblank':
    case 'flashcard': {
      const suffix = props.itemsCount > 1 ? 'exercices' : 'exercice';
      return `${props.itemsCount} ${suffix}`;
    }
    default:
      return null;
  }
});

const hasDiamond = computed(() => {
  if (categoryLockState.value.ancestorsLockState === 'unlocked') return false;
  if (
    ![UserContentType.MainContent, UserContentType.PreviousContent].includes(
      currentContent.value.type
    )
  ) {
    if (
      Object.prototype.hasOwnProperty.call(
        profile.lockedContent,
        props.category?.lockedContent || props.category?.childrenType
      )
    ) {
      return true;
    }
    return false;
  }
  return (
    profile.lockedContent[
      props.category?.lockedContent || props.category?.childrenType
    ] ?? false
  );
});

const mediaIcon = computed(() => props.category.mediaIcon);

const image = computed(() => {
  if (mPageMedias.value.has(mediaIcon.value))
    return (
      mPageMedias.value.get(mediaIcon.value).urlCompressed ||
      mPageMedias.value.get(mediaIcon.value).urlCompressed
    );

  return null;
});

const styleImage = computed(() => {
  if (!image.value) return null;
  return {
    backgroundImage: `url('${image.value}')`,
  };
});

const cssimage = computed(() => {
  if (!image.value) return null;
  return `url('${image.value}')`;
});
</script>
<style lang="scss" scoped>
.sub-section {
  display: grid;
  grid-template-columns: auto 1fr 3.6rem 2rem;
  width: 100%;
  min-height: 9.6rem;
  padding: var(--gap_m);
  color: var(--color_neutral_900);
  gap: var(--gap_s);
}

.logo {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 5rem;
  height: 5rem;
  .image {
    position: relative;
    z-index: 1;
    width: 4.5rem;
    height: 4.5rem;
    transition: transform 250ms $ease-out-quad, opacity 250ms $ease-out-quad 0ms;
    background-image: v-bind(cssimage);
    background-size: contain;
  }
  &.default {
    .image {
      background-image: url('~/assets/images/chapter/essential.svg');
    }
  }
  .shadow {
    position: absolute;
    z-index: 0;
    transform: translate(0, 0);
    filter: contrast(0%) grayscale(100%);
    opacity: 0;
  }
}

.arrow {
  position: relative;
  align-self: center;
  width: 2.4rem;
  height: 2.4rem;
  color: var(--color_secondary);
  .graphic {
    position: absolute;
    top: 0;
    left: 0;
    transform: translateX(0) scale(1);
    transition: transform 400ms $ease-out-back 200ms,
      opacity 50ms $ease-in-quad 200ms;
    opacity: 1;
  }
  .arrow-on {
    transform: translateX(-1.4rem) scale(0.8);
    transition: transform 400ms $ease-in-quad, opacity 50ms $ease-in-quad 100ms;
    opacity: 0;
  }
}

.as-header {
  grid-template-columns: auto 1fr 3.6rem;
  .arrow {
    display: none;
  }
}

.as-link {
  grid-template-columns: auto 1fr 3.6rem 2rem;
  .arrow {
    display: block;
  }
  &:hover,
  &:focus {
    .arrow-on {
      transform: translateX(0) scale(1);
      transition: transform 350ms $ease-out-back 200ms,
        opacity 80ms $ease-in-circ 200ms;
      opacity: 1;
    }
    .arrow-off {
      transform: translateX(1.8rem) scale(0.6);
      transition: transform 250ms $ease-in-back,
        opacity 100ms $ease-out-quad 160ms;
      opacity: 0;
    }
    .logo {
      .image {
        transform: translate(-0.1rem, -0.1rem) scale(1.1);
        transition: transform 450ms $ease-out-back-custom,
          opacity 450ms $ease-out-quad 0ms;
      }
      .shadow {
        transform: translate(0.3rem, 0.3rem) scale(1.09);
        opacity: 0.1;
      }
    }
  }
}

.details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  height: 100%;
}

.title {
  display: inline-flex;
  color: var(--color_neutral_900);
  gap: var(--gap_xxs);
  @include font('n', 600, 'secondary');
}

.description {
  display: inline-flex;
  align-items: center;
  margin-top: 0.2rem;
  color: var(--color_neutral_900);
  gap: var(--gap_xxs);
  @include font('s', 400, 'secondary');
}
.children-count {
  margin-top: 0.8rem;
  @include font('xs', 600, 'secondary');
}
</style>
